import { Building, GAME_CONFIG, RESOURCE_NODE_MAP } from '../constants';
import { CoreGameState } from '../GameEngine';
import { getBuildingPositions } from '../GameState';
import { collisionLogger } from '../Logger';

import {
  Entity,
  PlayerEntity,
  BuildingEntity,
  TerrainEntity,
  ResourceNodeEntity,
  EntityType,
  CollisionLayer,
  Position,
} from './CollisionTypes';

/**
 * Converts game state objects into collision system entities
 * This module bridges the gap between the existing game state and collision system
 */

/**
 * Convert player state to collision entity
 */
export function createPlayerEntity(gameState: CoreGameState): PlayerEntity {
  const { playerPos, playerFacing } = gameState;

  return {
    id: 'player',
    type: EntityType.PLAYER,
    position: playerPos,
    bounds: { x: playerPos.x, y: playerPos.y, width: 1, height: 1 },
    collisionLayer: CollisionLayer.PLAYER,
    collisionMask: [CollisionLayer.TERRAIN, CollisionLayer.BUILDINGS],
    isSolid: false, // Player can overlap with some entities
    isStatic: false,
    facing: playerFacing,
  };
}

/**
 * Convert building layer entries to collision entities
 */
export function createBuildingEntities(
  buildingLayer: Record<string, string>,
  buildingLookup: (character: string) => Building | null
): BuildingEntity[] {
  const entities: BuildingEntity[] = [];
  const processedPositions = new Set<string>();

  for (const [posKey, buildingChar] of Object.entries(buildingLayer)) {
    if (processedPositions.has(posKey)) continue;

    const building = buildingLookup(buildingChar);
    if (!building) continue;

    const [xStr, yStr] = posKey.split(',');
    if (!xStr || !yStr) continue;

    const centerX = parseInt(xStr, 10);
    const centerY = parseInt(yStr, 10);

    if (isNaN(centerX) || isNaN(centerY)) continue;

    // Use the shared getBuildingPositions function to calculate occupied positions
    const occupiedPositions = getBuildingPositions(centerX, centerY, building);
    const size = building.size || { width: 1, height: 1 };

    // Mark all positions as processed
    for (const pos of occupiedPositions) {
      processedPositions.add(`${pos.x},${pos.y}`);
    }

    // Calculate bounds from center position
    const offsetX = Math.floor(size.width / 2);
    const offsetY = Math.floor(size.height / 2);

    entities.push({
      id: `building_${centerX}_${centerY}`,
      type: EntityType.BUILDING,
      position: { x: centerX, y: centerY },
      bounds: {
        x: centerX - offsetX,
        y: centerY - offsetY,
        width: size.width,
        height: size.height,
      },
      collisionLayer: CollisionLayer.BUILDINGS,
      collisionMask: [
        CollisionLayer.TERRAIN,
        CollisionLayer.BUILDINGS,
        CollisionLayer.RESOURCES,
      ],
      isSolid: Boolean(building.blocking),
      isStatic: true,
      building,
      centerPosition: { x: centerX, y: centerY },
      occupiedPositions,
    });
  }

  return entities;
}

/**
 * Convert terrain map to collision entities
 */
export function createTerrainEntities(
  gameMap: Record<string, string>
): TerrainEntity[] {
  const entities: TerrainEntity[] = [];

  for (const [posKey, tileChar] of Object.entries(gameMap)) {
    // Skip empty tiles
    if (
      tileChar === GAME_CONFIG.EMPTY_CHARACTER ||
      tileChar === ' ' ||
      !tileChar
    ) {
      continue;
    }

    const [xStr, yStr] = posKey.split(',');
    if (!xStr || !yStr) continue;

    const x = parseInt(xStr, 10);
    const y = parseInt(yStr, 10);

    if (isNaN(x) || isNaN(y)) continue;

    const terrainType = getTerrainType(tileChar);
    const isSolid = isSolidTerrain(terrainType);

    entities.push({
      id: `terrain_${x}_${y}`,
      type: EntityType.TERRAIN,
      position: { x, y },
      bounds: { x, y, width: 1, height: 1 },
      collisionLayer: CollisionLayer.TERRAIN,
      collisionMask: [], // Terrain doesn't move, so no collision mask needed
      isSolid,
      isStatic: true,
      terrainType,
    });
  }

  // Debug logging to understand terrain entity detection
  if (collisionLogger.isEnabled()) {
    collisionLogger.debug('Terrain entity creation', {
      totalTilesChecked: Object.keys(gameMap).length,
      entitiesCreated: entities.length,
      sampleEntities: entities.slice(0, 5).map(e => ({
        id: e.id,
        terrainType: (e as TerrainEntity).terrainType,
        position: e.position,
      })),
    });
  }

  return entities;
}

/**
 * Convert resource nodes to collision entities using getTile function to include worldCache
 */
export function createResourceNodeEntities(
  gameMap: Record<string, string>,
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>,
    buildingLayer?: Record<string, string>
  ) => string | undefined,
  buildingLayer: Record<string, string>,
  playerPosition: Position,
  searchRadius: number = 100
): ResourceNodeEntity[] {
  const entities: ResourceNodeEntity[] = [];

  // Debug: Count resource tiles from different sources
  let gameMapResourceTiles = 0;
  let worldGeneratedResourceTiles = 0;
  let totalTilesChecked = 0;

  // Determine search bounds based on available world data
  const mapKeys = Object.keys(gameMap);
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const key of mapKeys) {
    const [xStr, yStr] = key.split(',');
    const x = Number.parseInt(xStr ?? '', 10);
    const y = Number.parseInt(yStr ?? '', 10);
    if (Number.isNaN(x) || Number.isNaN(y)) {
      continue;
    }
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    // Fallback to player position when no explicit map data exists
    minX = playerPosition.x - searchRadius;
    maxX = playerPosition.x + searchRadius;
    minY = playerPosition.y - searchRadius;
    maxY = playerPosition.y + searchRadius;
  } else {
    // Expand bounds to ensure we also consider generated world tiles near the player
    minX = Math.min(minX, playerPosition.x) - searchRadius;
    maxX = Math.max(maxX, playerPosition.x) + searchRadius;
    minY = Math.min(minY, playerPosition.y) - searchRadius;
    maxY = Math.max(maxY, playerPosition.y) + searchRadius;
  }

  const startX = Math.floor(minX);
  const endX = Math.ceil(maxX);
  const startY = Math.floor(minY);
  const endY = Math.ceil(maxY);

  for (let x = startX; x <= endX; x++) {
    for (let y = startY; y <= endY; y++) {
      totalTilesChecked++;

      // Use getTile to get the actual tile (including worldCache)
      const tileChar = getTile(x, y, gameMap, buildingLayer);
      if (!tileChar) continue;

      const resourceConfig = RESOURCE_NODE_MAP[tileChar];
      if (!resourceConfig) continue;

      const resourceType = getResourceType(tileChar);
      if (!resourceType) continue;

      // Track resource source for debugging
      const key = `${x},${y}`;
      if (key in gameMap) {
        gameMapResourceTiles++;
      } else {
        worldGeneratedResourceTiles++;
      }

      entities.push({
        id: `resource_${x}_${y}`,
        type: EntityType.RESOURCE_NODE,
        position: { x, y },
        bounds: { x, y, width: 1, height: 1 },
        collisionLayer: CollisionLayer.RESOURCES,
        collisionMask: [], // Resources don't move
        isSolid: resourceConfig.isSolid, // Use configuration to determine if resource blocks movement
        isStatic: true,
        resourceType,
      });
    }
  }

  // Debug logging to understand resource node detection
  if (collisionLogger.isEnabled()) {
    collisionLogger.debug('Resource node entity creation', {
      totalTilesChecked,
      gameMapResourceTiles,
      worldGeneratedResourceTiles,
      entitiesCreated: entities.length,
      searchRadius,
      gameConfig: {
        iron: GAME_CONFIG.IRON_NODE_CHARACTER,
        copper: GAME_CONFIG.COPPER_NODE_CHARACTER,
      },
      sampleResources: entities.slice(0, 10).map(e => ({
        id: e.id,
        position: e.position,
        resourceType: e.resourceType,
      })),
    });
  }

  return entities;
}

/**
 * Get all collision entities from game state
 */
export function getAllEntitiesFromGameState(
  gameState: CoreGameState,
  buildingLookup: (character: string) => Building | null,
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>,
    buildingLayer?: Record<string, string>
  ) => string | undefined
): Entity[] {
  const entities: Entity[] = [];

  // Add player
  entities.push(createPlayerEntity(gameState));

  // Add buildings
  entities.push(
    ...createBuildingEntities(gameState.buildingLayer, buildingLookup)
  );

  // Add terrain
  entities.push(...createTerrainEntities(gameState.gameMap));

  // Add resource nodes - now using getTile to include worldCache
  entities.push(
    ...createResourceNodeEntities(
      gameState.gameMap,
      getTile,
      gameState.buildingLayer,
      gameState.playerPos
    )
  );

  return entities;
}

/**
 * Helper: Determine terrain type from character
 */
function getTerrainType(tileChar: string): string {
  switch (tileChar) {
    case '▓':
    case '█':
      return 'wall';
    case '~':
      return 'water';
    case '^':
    case '⛰️':
      return 'mountain';
    case '🌲':
    case 'T':
      return 'tree';
    case '●':
    case 'O':
      return 'rock';
    case '.':
    case ' ':
      return 'grass';
    default:
      // Check if it's a resource node (will be handled separately)
      if (getResourceType(tileChar)) {
        return 'grass'; // Resource nodes sit on grass
      }
      return 'unknown';
  }
}

/**
 * Helper: Check if terrain type is solid (blocks movement)
 */
function isSolidTerrain(terrainType: string): boolean {
  return ['wall', 'water', 'mountain', 'tree', 'rock'].includes(terrainType);
}

/**
 * Helper: Get resource type from character
 */
function getResourceType(tileChar: string): 'iron' | 'copper' | null {
  switch (tileChar) {
    case GAME_CONFIG.IRON_NODE_CHARACTER:
      return 'iron';
    case GAME_CONFIG.COPPER_NODE_CHARACTER:
      return 'copper';
    default:
      return null;
  }
}

/**
 * Helper: Update collision entity position
 */
export function updateEntityPosition(
  entity: Entity,
  newPosition: Position
): Entity {
  const newBounds = {
    ...entity.bounds,
    x: newPosition.x,
    y: newPosition.y,
  };

  return {
    ...entity,
    position: newPosition,
    bounds: newBounds,
  };
}
